import { Navigate, useParams } from 'react-router-dom';
import Container from '~/App/components/Container';
import i18n from '~/i18n';
import Header from '~/App/components/Header';
import { Suspense, useMemo, useState } from 'react';
import OrderInfo from './components/OrderInfo';
import OrderDetailItemRow from './components/OrderDetailItemRow';
import TotalSection from './components/TotalSection';
import BottomBar from './components/BottomBar';
import PageContext from './context';
import { useLoadingSetter } from '~/providers/LoadingProvider';
import FixedBottom from '../../../components/FixedBottom';
import StickyHeader from '../../../components/StickyHeader';
import { Button, Divider, Typography } from 'antd';
import { usePrintOrder } from '~/App/hooks';
import Tax from './components/Tax';
import clsx from 'clsx';
import TransparentOverlay from '~/App/components/TransparentOverlay';
import ErrorBoundary from '~/App/ErrorBoundary';
import Loading from '~/App/components/Loading';
import { useSuspenseQuery } from '@tanstack/react-query';
import { api } from '@restaurant/api';
import { OrderType } from '@restaurant/types';
import PaymentHistory from './components/PaymentHistory';

const OrderDetailPageContent = ({ id }: { id: string }) => {
  const { data: order, refetch } = useSuspenseQuery({
    queryKey: ['fetchOrderById', id],
    queryFn: () => {
      return api.fetchOrderById(id);
    },
  });

  const [paid, setPaid] = useState(false);
  const setLoading = useLoadingSetter();
  const { printOrderBatch } = usePrintOrder();

  const contextValue = useMemo(() => {
    return { refresh: refetch, paid, setPaid };
  }, [refetch, paid, setPaid]);

  const onPrintBatch = async (index: number) => {
    if (!id) return;
    try {
      setLoading(true);
      await printOrderBatch(id, index);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContext.Provider value={contextValue}>
      <Container>
        <TransparentOverlay show={paid} />
        <StickyHeader>
          <Header
            title={`${i18n.t('order_detail_page_header')} - No. ${
              order.orderNumber
            }`}
          />
          <OrderInfo order={order} isPaid={paid} />
        </StickyHeader>
        <div style={{ paddingBottom: 400 }}>
          {order.batches.map((batch, batchIndex) => {
            return (
              <div
                key={`${batchIndex}`}
                className={clsx({
                  'bg-red-100': batch.every(({ isChecked }) => isChecked),
                })}
              >
                <div className="flex justify-content-between px-4 pt-1">
                  <Typography.Text type="secondary">
                    {`#${batchIndex + 1}`}
                  </Typography.Text>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => onPrintBatch(batchIndex)}
                  >
                    Print Batch
                  </Button>
                </div>
                {batch.map((item, itemIndex) => (
                  <OrderDetailItemRow
                    orderId={order.orderId}
                    item={item}
                    batchIndex={batchIndex}
                    itemIndex={itemIndex}
                    key={`${batchIndex}-${item.menuId}`}
                  />
                ))}
                <Divider
                  style={{
                    marginTop: 0,
                    marginBottom: 0,
                    backgroundColor: '#ddd',
                  }}
                />
              </div>
            );
          })}
          <Tax order={order} setLoading={setLoading} onSuccess={refetch} />
        </div>
        <FixedBottom>
          <TotalSection order={order} />
          {order.orderType === OrderType.Indent ? (
            <PaymentHistory order={order} />
          ) : null}
          <BottomBar order={order} />
        </FixedBottom>
      </Container>
    </PageContext.Provider>
  );
};

const OrderDetailPage = () => {
  const { id } = useParams();
  if (!id) return <Navigate to="/order" />;

  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading />}>
        <OrderDetailPageContent id={id} />
      </Suspense>
    </ErrorBoundary>
  );
};

export default OrderDetailPage;
